import Button from "@/components/common/Button";
import { useLoggedInCompany } from "@/hooks/queries/useCompanyQueries";
import { useCourses } from "@/hooks/queries/useCourseQueries";
import { Department } from "@/hooks/queries/useDepartmentQueries";
import {
  useActivateDepartment,
  useAddEmployeeToDepartment,
  useAssignDepartmentAdmin,
  useCreateDepartment,
  useDeactivateDepartment,
  useRemoveEmployeeFromDepartment,
  useRevokeDepartmentAdmin,
} from "@/hooks/queries/useDepartmentMutations";
import { useDepartmentData } from "@/hooks/queries/useDepartmentData";
import { useAssignCourseToDepartment } from "@/hooks/queries/useCourseAssignmentMutations";
import { useAuth } from "@/hooks/useAuth";
import { AcademicCapIcon } from "@heroicons/react/24/outline";
import React from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { formatDate, matchesSearch } from "@/utils/formatters";
import {
  useDepartmentModals,
  useDepartmentForm,
  useDepartmentData as useDepartmentDataState,
  useDepartmentFilters,
  useDepartmentLoading,
} from "@/hooks/useDepartmentManagement";

// Define the employee type from company data
interface CompanyEmployee {
  _id: string;
  id?: string;
  fullName?: string;
  name?: string;
  email: string;
  role?: string;
  departmentId?: string;
  isActive?: boolean;
  isApproved?: boolean;
  companyId?: string;
}

const Departments = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const companyUser = user as any;

  // Use custom hooks for state management
  const {
    showModal,
    setShowModal,
    showDetailModal,
    setShowDetailModal,
    selectedDepartment,
    setSelectedDepartment,
  } = useDepartmentModals();

  const {
    newDeptName,
    setNewDeptName,
    newDeptDesc,
    setNewDeptDesc,
    selectedAdminId,
    setSelectedAdminId,
  } = useDepartmentForm();

  const {
    departmentEmployees,
    setDepartmentEmployees,
    departmentCourses,
    setDepartmentCourses,
    assigningCourseIds,
    setAssigningCourseIds,
  } = useDepartmentDataState();

  const {
    searchTerm,
    setSearchTerm,
    employeeSearch,
    setEmployeeSearch,
  } = useDepartmentFilters();

  const {
    loadingDepartments,
    setLoadingDepartments,
    loadingEmployees,
    setLoadingEmployees,
    loadingAdmins,
    setLoadingAdmins,
  } = useDepartmentLoading();

  // Get department data for selected department
  const {
    data: selectedDepartmentData,
  } = useDepartmentData(selectedDepartment?._id || "", !!selectedDepartment?._id);

  // Get company data including employees
  const {
    data: companyData,
    error: companyError,
    isLoading: companyLoading,
    refetch: refetchCompany,
  } = useLoggedInCompany();

  // Get courses data
  const { data: allCourses = [] } = useCourses();

  // Course assignment mutation
  const assignCourseMutation = useAssignCourseToDepartment();

  // Update department courses when selected department data changes
  React.useEffect(() => {
    if (selectedDepartmentData?.data?.document?.coursesAssignedToDepartment) {
      setDepartmentCourses(selectedDepartmentData.data.document.coursesAssignedToDepartment);
    } else {
      setDepartmentCourses([]);
    }
  }, [selectedDepartmentData]);

  // Mutation hooks
  const createDepartment = useCreateDepartment();
  const deactivateDepartment = useDeactivateDepartment();
  const activateDepartment = useActivateDepartment();
  const removeEmployeeFromDepartment = useRemoveEmployeeFromDepartment();
  const addEmployeeToDepartment = useAddEmployeeToDepartment();
  const revokeDepartmentAdmin = useRevokeDepartmentAdmin();
  const assignDepartmentAdmin = useAssignDepartmentAdmin();

  // Use departments from companyData, mapping to Department type
  const departments = (companyData?.data?.document?.departments || []).map((dept: any) => {
      return {
        _id: dept._id || dept.id,
        name: dept.name,
        description: dept.description,
        companyId: companyUser?._id || "",
        employees: dept.employees || [],
        coursesAssignedToDepartment:
          dept.coursesAssignedToDepartment ||
          dept.courses ||
          dept.assignedCourses ||
          [],
        createdAt: dept.createdAt || new Date().toISOString(),
        isActive: dept.isActive !== undefined ? dept.isActive : true,
        departmentAdmin: dept.departmentAdmin,
      };
  });

  // Extract employees from company data - handle case where company data is not available
  let employeesData = (companyData?.data?.document?.employees || []).map((emp: any) => ({
      _id: emp._id || emp.id,
      id: emp.id,
      fullName: emp.fullName,
      name: emp.name,
      email: emp.email,
      role: emp.role,
      departmentId: emp.departmentId,
      isActive: emp.isActive,
      isApproved: emp.isApproved,
  }));

  // For department_admin, filter to only show employees in the same company
  if (user?.role === "department_admin" && companyUser?.companyId) {
    employeesData = employeesData.filter(
      (emp: any) => emp.companyId === companyUser.companyId
    );
  }

  // Filter departments by search term and ensure no duplicates
  const filteredDepartments = (departments || [])
    .filter((dept) =>
      matchesSearch(dept?.name || "", searchTerm)
    )
    // Additional filter to ensure no duplicates
    .filter(
      (dept, index, self) => index === self.findIndex((d) => d._id === dept._id)
    );

  // Get department admin for a specific department
  const getDepartmentAdmin = (dept: Department): CompanyEmployee | null => {
    const deptEmployees = employeesData.filter(
      (emp) => emp.departmentId === dept._id
    );
    return deptEmployees.find(
      (emp) =>
        emp.role === "departmentAdmin" ||
        emp.role === "department_admin"
    ) || null;
  };

  const handleAddDepartment = () => {
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setNewDeptName("");
    setNewDeptDesc("");
  };

  const handleCreateDepartment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newDeptName.trim() || !companyUser?._id) return;
    try {
      const payload: any = {
        name: newDeptName,
        company: companyUser._id,
      };
      if (newDeptDesc.trim()) {
        payload.description = newDeptDesc;
      }
      await createDepartment.mutateAsync(payload);
      toast.success(t("admin.departmentAdded"));
      
      // Reset form fields
      setNewDeptName("");
      setNewDeptDesc("");
      
      // Close modal
      handleCloseModal();
      
      // Refresh data after creating department
      await refetchCompany();
    } catch (error: any) {
      toast.error(
        error instanceof Error ? error.message : t("admin.departmentAddFailed")
      );
    }
  };

  // Handle department detail modal
  const handleViewDetails = async (dept: Department) => {
    setSelectedDepartment(dept);
    setShowDetailModal(true);

    // Department data will be fetched by the useDepartmentData hook

    // Set employees from company data instead of department API
    const deptEmployees = employeesData.filter(
      (emp) => emp.departmentId === dept._id
    );
    setDepartmentEmployees(deptEmployees);
  };

  const handleCloseDetailModal = () => {
    setShowDetailModal(false);
    setSelectedDepartment(null);
    setDepartmentEmployees([]);
    setDepartmentCourses([]);
  };

  // Handle department actions
  const handleDeactivateDepartment = async (dept: Department) => {
    if (!dept.isActive || !dept._id) return;
    setLoadingDepartments((prev) => new Set(prev).add(dept._id));
    try {
      await deactivateDepartment.mutateAsync(dept._id);
      toast.success(t("admin.departmentDeactivated"));
      setTimeout(() => {
        refetchCompany();
      }, 1000);
    } catch (error: any) {
      toast.error(
        error instanceof Error ? error.message : t("admin.departmentDeactivateFailed")
      );
    } finally {
      setLoadingDepartments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dept._id);
        return newSet;
      });
    }
  };

  const handleActivateDepartment = async (dept: Department) => {
    if (dept.isActive || !dept._id) return;
    setLoadingDepartments((prev) => new Set(prev).add(dept._id));
    try {
      await activateDepartment.mutateAsync(dept._id);
      toast.success(t("admin.departmentActivated"));
      setTimeout(() => {
        refetchCompany();
      }, 1000);
    } catch (error: any) {
      toast.error(
        error instanceof Error ? error.message : t("admin.departmentActivateFailed")
      );
    } finally {
      setLoadingDepartments((prev) => {
        const newSet = new Set(prev);
        newSet.delete(dept._id);
        return newSet;
      });
    }
  };


  // Handle adding employee to department
  const handleAddEmployee = async (employeeId: string) => {
    if (!selectedDepartment?._id) return;
    try {
      await addEmployeeToDepartment.mutateAsync({
        departmentId: selectedDepartment._id,
        employeeId: employeeId,
      });
      toast.success(t("admin.employeeAdded", "Employee added successfully"));
      
      // Refresh department data after adding employee
      const deptEmployees = employeesData.filter(
        (emp) => emp.departmentId === selectedDepartment._id
      );
      setDepartmentEmployees(deptEmployees);
      
      // Refresh company data
      refetchCompany();
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      if (errorMessage.includes("already assigned")) {
        toast.error(t("admin.employeeAlreadyAssigned", "Employee is already assigned to this department"));
      } else {
        toast.error(error instanceof Error ? error.message : t("admin.addEmployeeFailed", "Failed to add employee"));
      }
    }
  };

  // Handle assigning admin
  const handleAssignAdmin = async (employeeId: string) => {
    if (!selectedDepartment?._id) return;
    setLoadingAdmins((prev) => new Set(prev).add(selectedDepartment._id));
    try {
      await assignDepartmentAdmin.mutateAsync({
        departmentId: selectedDepartment._id,
        employeeId: employeeId,
      });
      toast.success(t("admin.adminAssigned", "Admin assigned successfully"));
      
      // Update department employees immediately by adding admin role
      setDepartmentEmployees(prev => 
        prev.map(emp => 
          emp._id === employeeId 
            ? { ...emp, role: 'departmentAdmin' } 
            : emp
        )
      );
      
      // Refresh company data
      refetchCompany();
      
      // Clear selected admin ID
      setSelectedAdminId("");
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error.message;
      if (errorMessage.includes("already a department admin")) {
        toast.error(t("admin.employeeAlreadyAdmin", "Employee is already a department admin"));
      } else {
        toast.error(error instanceof Error ? error.message : t("admin.assignAdminFailed", "Failed to assign admin"));
      }
    } finally {
      setLoadingAdmins((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedDepartment._id);
        return newSet;
      });
    }
  };

  // Handle revoking admin
  const handleRevokeAdmin = async (admin: CompanyEmployee) => {
    if (!selectedDepartment?._id) return;
    setLoadingAdmins((prev) => new Set(prev).add(selectedDepartment._id));
    try {
      await revokeDepartmentAdmin.mutateAsync({
        departmentId: selectedDepartment._id,
        employeeId: admin._id,
      });
      toast.success(t("admin.adminRevoked", "Admin revoked successfully"));
      
      // Update department employees immediately by removing admin role
      setDepartmentEmployees(prev => 
        prev.map(emp => 
          emp._id === admin._id 
            ? { ...emp, role: 'employee' } 
            : emp
        )
      );
      
      // Refresh company data
      refetchCompany();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          t("admin.revokeAdminFailed", "Failed to revoke admin")
      );
    } finally {
      setLoadingAdmins((prev) => {
        const newSet = new Set(prev);
        newSet.delete(selectedDepartment._id);
        return newSet;
      });
    }
  };


  const handleRemoveEmployee = async (employeeId: string) => {
    if (!selectedDepartment?._id) return;
    setLoadingEmployees((prev) => new Set(prev).add(employeeId));
    try {
      await removeEmployeeFromDepartment.mutateAsync({
        departmentId: selectedDepartment._id,
        employeeId: employeeId,
      });
      toast.success(t("admin.employeeRemoved", "Employee removed from department successfully"));
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } catch (error: any) {
      toast.error(error instanceof Error ? error.message : t("admin.removeEmployeeFailed"));
    } finally {
      setLoadingEmployees((prev) => {
        const newSet = new Set(prev);
        newSet.delete(employeeId);
        return newSet;
      });
    }
  };

  // Handle course assignment
  const handleAssignCourse = async (courseId: string) => {
    if (!selectedDepartment?._id) return;
    setAssigningCourseIds((prev) => [...prev, courseId]);
    try {
      await assignCourseMutation.mutateAsync({
        departmentId: selectedDepartment._id,
        courseId: courseId,
      });
      toast.success(t("admin.courseAssigned", "Course assigned to department successfully"));
      
      // Update department courses immediately
      setDepartmentCourses(prev => [...prev, courseId]);
      
      // Refresh company data
      refetchCompany();
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message ||
          error.message ||
          t("admin.courseAssignFailed", "Failed to assign course")
      );
    } finally {
      setAssigningCourseIds((prev) => prev.filter((id) => id !== courseId));
    }
  };

  // Handler for search term change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Handler for department name change
  const handleDepartmentNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewDeptName(e.target.value);
  };

  // Handler for department description change
  const handleDepartmentDescChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewDeptDesc(e.target.value);
  };

  // Handler for admin selection change
  const handleAdminSelectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedAdminId(e.target.value);
  };

  // Handler for employee search change
  const handleEmployeeSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmployeeSearch(e.target.value);
  };

  // Handler for removing employee with loading state
  const handleRemoveEmployeeWithLoading = (emp: CompanyEmployee) => {
    const employeeId = emp._id || emp.id;
    if (employeeId) {
      setLoadingEmployees((prev) => new Set(prev).add(employeeId));
      handleRemoveEmployee(employeeId);
    }
  };

  if (companyLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (companyError) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">{t("common.error")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("admin.allDepartments")}
          </h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            {t("admin.manageDepartments")}
                          </p>
                        </div>
            </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
        <div className="flex-1 max-w-md">
          <input
            type="text"
            placeholder={t("admin.searchDepartments")}
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:text-white"
          />
                  </div>
        <div className="flex gap-3">
                          <Button
                            variant="outline"
            className="px-4 py-2"
          >
            {t("admin.filter")}
                          </Button>
          <Button
            variant="primary"
            onClick={handleAddDepartment}
            className="px-4 py-2"
          >
            + {t("admin.addDepartment")}
          </Button>
                </div>
              </div>

      {/* Departments Table */}
      {filteredDepartments.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.department")}
              </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.description")}
              </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.admin")}
              </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.status")}
              </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t("admin.action")}
              </th>
            </tr>
          </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredDepartments.map((dept) => {
                  const admin = getDepartmentAdmin(dept);
  return (
                    <tr key={dept._id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                  {dept.name}
                        </div>
                </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {(dept as any).description || t("admin.noDescription")}
                        </div>
                </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900 dark:text-white">
                          {admin ? admin.fullName || admin.name || "Unknown" : t("admin.noAdmin")}
                        </div>
                </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      dept.isActive
                            ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                            : "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200"
                        }`}>
                          {dept.isActive ? t("admin.active") : t("admin.inactive")}
                  </span>
                </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                            onClick={() => handleViewDetails(dept)}
                            className="flex items-center space-x-1"
                          >
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                            <span>{t("admin.view")}</span>
                  </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => dept.isActive ? handleDeactivateDepartment(dept) : handleActivateDepartment(dept)}
                            disabled={loadingDepartments.has(dept._id)}
                            className="flex items-center space-x-1"
                          >
                            {loadingDepartments.has(dept._id) ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            ) : (
                              <span>{dept.isActive ? t("admin.deactivate") : t("admin.activate")}</span>
                            )}
                          </Button>
                        </div>
                </td>
              </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
        </div>
      ) : (
        <div className="text-center py-12">
          <AcademicCapIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
            {t("admin.noDepartments")}
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {t("admin.getStartedByCreating")}
          </p>
          <div className="mt-6">
            <Button
              variant="primary"
              onClick={() => setShowModal(true)}
            >
              {t("admin.createDepartment")}
            </Button>
          </div>
        </div>
      )}

      {/* Create Department Modal */}
      {showModal && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative bg-slate-800 rounded-xl shadow-2xl w-full max-w-2xl mx-4 p-8">
            <button
              className="absolute top-4 right-4 text-gray-300 hover:text-white text-2xl"
              onClick={handleCloseModal}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-2xl font-semibold text-white mb-6">
              {t("admin.addDepartment", "Add New Department")}
            </h2>
            <form onSubmit={handleCreateDepartment}>
              <div className="mb-4">
                <label className="block text-white font-medium mb-2">
                  {t("admin.departmentName", "Department Name")}
                </label>
                <input
                  type="text"
                  className="w-full rounded-md bg-slate-700 border border-slate-600 text-white px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t("admin.departmentNamePlaceholder", "Enter department name")}
                  value={newDeptName}
                  onChange={handleDepartmentNameChange}
                  required
                />
              </div>
              <div className="mb-8">
                <label className="block text-white font-medium mb-2">
                  {t("admin.description", "Description")}
                </label>
                <textarea
                  className="w-full rounded-md bg-slate-700 border border-slate-600 text-white px-4 py-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t("admin.descriptionPlaceholder", "Description")}
                  value={newDeptDesc}
                  onChange={handleDepartmentDescChange}
                />
              </div>
              <div className="flex justify-end">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={createDepartment.isPending}
                >
                  {createDepartment.isPending
                    ? t("common.loading")
                    : t("admin.createDepartment", "Create Department")}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Department Detail Modal */}
      {showDetailModal && selectedDepartment && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black bg-opacity-60">
          <div className="relative bg-slate-800 rounded-xl shadow-2xl w-full max-w-4xl mx-4 p-8 max-h-[90vh] overflow-y-auto">
            <button
              className="absolute top-4 right-4 text-gray-300 hover:text-white text-2xl"
              onClick={handleCloseDetailModal}
              aria-label="Close"
            >
              &times;
            </button>
            <h2 className="text-2xl font-semibold text-white mb-6">
              {selectedDepartment.name} - {t("admin.departmentDetails", "Department Details")}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Department Info */}
              <div className="bg-slate-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {t("admin.departmentInfo", "Department Information")}
                </h3>
                <div className="space-y-2 text-gray-300">
                  <p><span className="font-medium">Name:</span> {selectedDepartment.name}</p>
                  <p>
                    <span className="font-medium">Status:</span>
                    <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                        selectedDepartment.isActive
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                    }`}>
                      {selectedDepartment.isActive ? t("common.active", "Active") : t("common.inactive", "Inactive")}
                    </span>
                  </p>
                  <p><span className="font-medium">Created:</span> {formatDate(selectedDepartment.createdAt)}</p>
                  <p><span className="font-medium">Employees:</span> {departmentEmployees.length}</p>
                </div>
              </div>

              {/* Department Admin */}
              <div className="bg-slate-700 p-4 rounded-lg">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {t("admin.departmentAdmin", "Department Admin")}
                </h3>
                {(() => {
                  const admin = departmentEmployees.find(
                    (emp) => emp.role === "departmentAdmin" || emp.role === "department_admin"
                  );
                  
                  if (admin) {
                    return (
                  <div className="space-y-2 text-gray-300">
                        <p><span className="font-medium">Name:</span> {admin.fullName || admin.name}</p>
                        <p><span className="font-medium">Email:</span> {admin.email}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 border-red-600 text-red-400 hover:bg-red-900/20"
                          disabled={loadingAdmins.has(selectedDepartment._id || "")}
                          onClick={() => handleRevokeAdmin(admin)}
                        >
                          {loadingAdmins.has(selectedDepartment._id || "") ? (
                            <span className="inline-flex items-center gap-1">
                              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              {t("common.loading", "Loading")}
                            </span>
                          ) : (
                            t("admin.revokeAdmin", "Revoke Admin")
                          )}
                        </Button>
                  </div>
                    );
                  } else {
                    return (
                  <div className="text-gray-400">
                        <p className="mb-2">{t("admin.noAdminAssigned", "No admin assigned")}</p>
                    <div className="flex gap-2">
                      <select
                        className="w-full bg-slate-600 border border-slate-500 text-white px-3 py-2 rounded-md"
                        value={selectedAdminId}
                        onChange={handleAdminSelectionChange}
                      >
                            <option value="">{t("admin.selectEmployee", "Select Employee")}</option>
                        {departmentEmployees.map((emp) => (
                          <option key={emp._id} value={emp._id}>
                                {emp.fullName || emp.name} ({emp.email})
                          </option>
                        ))}
                      </select>
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={!selectedAdminId || loadingAdmins.has(selectedDepartment._id || "")}
                            onClick={() => handleAssignAdmin(selectedAdminId)}
                          >
                            {loadingAdmins.has(selectedDepartment._id || "") ? (
                              <span className="inline-flex items-center gap-1">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t("common.loading", "Loading")}
                              </span>
                            ) : (
                              t("admin.add", "Add")
                            )}
                          </Button>
                    </div>
                  </div>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Employees List */}
            <div className="mt-6 bg-slate-700 p-4 rounded-lg">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
                <h3 className="text-lg font-semibold text-white">
                  {t("admin.employees", "Employees")}
                </h3>
                <input
                  type="text"
                  placeholder={t("admin.searchEmployees", "Search employees")}
                  className="w-full md:w-64 rounded-md border border-slate-500 bg-slate-600 text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  value={employeeSearch}
                  onChange={handleEmployeeSearchChange}
                />
              </div>

              {/* Available Employees to Add */}
              <div className="mb-6">
                <h4 className="text-md font-medium text-white mb-3">
                  {t("admin.availableEmployees", "Available Employees to Add")}
                </h4>
                {(() => {
                  const availableEmployees = employeesData.filter(
                    (emp) => !emp.departmentId || emp.departmentId === ""
                  );
                  const filteredEmployees = availableEmployees.filter(
                    (emp) =>
                      matchesSearch(emp.fullName || "", employeeSearch) ||
                      matchesSearch(emp.email || "", employeeSearch)
                  );

                  if (filteredEmployees.length > 0) {
                    return (
                      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {filteredEmployees.map((emp) => (
                          <div key={emp._id} className="flex items-center justify-between bg-slate-600 p-3 rounded">
                            <div>
                              <p className="text-white font-medium">{emp.fullName || emp.name}</p>
                              <p className="text-gray-400 text-sm">{emp.email}</p>
                              <p className="text-xs mt-1 text-yellow-400">{t("admin.unassigned", "Unassigned")}</p>
                            </div>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleAddEmployee(emp._id)}
                            >
                              {t("admin.add", "Add")}
                            </Button>
                          </div>
                        ))}
                      </div>
                    );
                  } else {
                    return (
                      <div className="text-gray-400 text-center py-4">
                        {t("admin.noAvailableEmployees", "No unassigned employees available to add.")}
                      </div>
                    );
                  }
                })()}
              </div>

              {/* Current Department Employees */}
              <div className="mt-6">
                <h4 className="text-md font-medium text-white mb-3">
                  {t("admin.currentEmployees", "Current Department Employees")} ({departmentEmployees.length})
                </h4>
                {departmentEmployees.length > 0 ? (
                  <div className="space-y-2">
                    {departmentEmployees.map((emp, empIdx) => (
                      <div key={`${emp._id || emp.id}-${empIdx}`} className="flex justify-between items-center bg-slate-600 p-3 rounded">
                        <div>
                          <p className="text-white font-medium">{emp.fullName || emp.name}</p>
                          <p className="text-gray-400 text-sm">{emp.email}</p>
                          <p className="text-xs mt-1">
                            {t("admin.approvalStatus", "Status")}: {emp.isApproved ? t("admin.approved", "Approved") : t("admin.notApproved", "Not Approved")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            emp.role === "departmentAdmin" || emp.role === "department_admin"
                                ? "bg-blue-100 text-blue-800"
                                : "bg-gray-100 text-gray-800"
                          }`}>
                            {emp.role}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            className="border-red-600 text-red-400 hover:bg-red-900/20"
                            disabled={loadingEmployees.has(emp._id || emp.id || "")}
                            onClick={() => handleRemoveEmployeeWithLoading(emp)}
                          >
                            {loadingEmployees.has(emp._id || emp.id || "") ? (
                              <span className="inline-flex items-center gap-1">
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {t("common.loading", "Loading")}
                              </span>
                            ) : (
                              t("common.remove", "Remove")
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400">{t("admin.noEmployees", "No employees found")}</p>
                )}
              </div>
            </div>

            {/* Already Assigned Courses Section */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">
                {t("admin.assignedCourses", "Already Assigned Courses")}
              </h3>
              {departmentCourses.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-6">
                  {departmentCourses.map((courseId) => {
                    const course = allCourses.find((c) => c._id === courseId);
                    return course ? (
                      <div key={courseId} className="flex items-center justify-between bg-green-600 p-3 rounded">
                        <div>
                          <p className="text-white font-medium">{course.title?.en}</p>
                          <p className="text-green-100 text-sm">{course.courseDescription?.en}</p>
                        </div>
                        <span className="text-green-100 text-sm font-medium">
                          ✓ {t("admin.assigned", "Assigned")}
                        </span>
                      </div>
                    ) : null;
                  })}
                </div>
              ) : (
                <div className="text-gray-400 text-center py-4">
                  {t("admin.noAssignedCourses", "No courses assigned to this department yet.")}
                </div>
              )}
            </div>

            {/* Course Assignment Section - Company Admin Only */}
            {user?.role === "company_admin" && (
              <div className="mt-8">
                <h3 className="text-lg font-semibold text-white mb-4">
                  {t("admin.assignCourses", "Assign Courses to Department")}
                </h3>
                {allCourses.length === 0 ? (
                  <div className="text-gray-400 text-center py-8">
                    {t("admin.noCoursesForCompany", "There are no courses purchased for the company.")}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {allCourses.map((course) => {
                      const isAssigned = departmentCourses.includes(course._id);
                      return (
                        <div key={course._id} className="flex items-center justify-between bg-slate-600 p-3 rounded">
                          <div>
                            <p className="text-white font-medium">{course.title?.en}</p>
                            <p className="text-gray-400 text-sm">{course.courseDescription?.en}</p>
                          </div>
                          <Button
                            variant={isAssigned ? "outline" : "primary"}
                            size="sm"
                            disabled={isAssigned || assigningCourseIds.includes(course._id)}
                            onClick={() => handleAssignCourse(course._id)}
                          >
                            {isAssigned
                              ? t("admin.assigned", "Assigned")
                              : assigningCourseIds.includes(course._id)
                              ? t("common.loading")
                              : t("admin.assign", "Assign")}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
        </div>
        </div>
      )}
    </div>
  );
};

export default Departments;
